// Everything the pages call out to, in one React context. main.tsx builds the
// real set; tests inject fakes (no network, no LiveKit, no camera).
import { createContext, useContext } from 'react';
import type { ComponentType, ReactNode } from 'react';
import type { ApiClient, ApiError } from './api/client.js';
import type { AuthPort } from './auth/port.js';

export interface LiveRoomProps {
  serverUrl: string;
  token: string;
  audio: boolean;
  video: boolean;
  audioDeviceId: string | null;
  videoDeviceId: string | null;
  onLeave: () => void;
  /** In-room controls rendered inside the LiveKit room (the "Gravar" control). */
  controls?: ReactNode;
  /** A peer announced a recording start on RECORDING_DATA_TOPIC (best effort). */
  onRecordingSignal?: () => void;
  /** Receives the sender for RECORDING_DATA_TOPIC once in the room; null on unmount. */
  bindRecordingSignal?: (send: (() => void) | null) => void;
}

/** The subset of MediaDevices the pre-join uses; null when the browser has none. */
export type MediaDevicesPort = Pick<MediaDevices, 'getUserMedia' | 'enumerateDevices'>;

export interface AuthFailureBus {
  emit(err: ApiError): void;
  subscribe(listener: (err: ApiError) => void): () => void;
}

export function createAuthFailureBus(): AuthFailureBus {
  const listeners = new Set<(err: ApiError) => void>();
  return {
    emit: (err) => listeners.forEach((listener) => listener(err)),
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

export interface Services {
  auth: AuthPort;
  api: ApiClient;
  authFailures: AuthFailureBus;
  /** The LiveKit room; the only component that loads livekit-client. */
  LiveRoom: ComponentType<LiveRoomProps>;
  media: MediaDevicesPort | null;
  confirm: (message: string) => boolean;
}

const ServicesContext = createContext<Services | null>(null);

export function ServicesProvider({ services, children }: { services: Services; children: ReactNode }) {
  return <ServicesContext.Provider value={services}>{children}</ServicesContext.Provider>;
}

export function useServices(): Services {
  const services = useContext(ServicesContext);
  if (!services) throw new Error('ServicesProvider missing');
  return services;
}
